import type {
  BuilderLegalStructure,
  BuilderLegalStructureResponse,
  SaveBuilderLegalStructureRequest,
} from "@/lib/api/databook";
import { BackendError } from "@/lib/backend";

import { legalStructuresHaveSameWritableState } from "./legalStructure";

export interface LegalStructureAdapterState {
  editing: boolean;
  dirty: boolean;
  saving: boolean;
  draft: BuilderLegalStructure | null;
  undoStack: BuilderLegalStructure[];
  error: string | null;
}

export type LegalStructureAdapterAction =
  | { type: "edit"; structure: BuilderLegalStructure }
  | {
      type: "change";
      structure: BuilderLegalStructure;
      persistedStructure: BuilderLegalStructure;
    }
  | {
      type: "undo";
      persistedStructure: BuilderLegalStructure;
    }
  | { type: "cancel" }
  | { type: "save_started" }
  | { type: "save_failed"; error: string }
  | { type: "save_succeeded" };

export const initialLegalStructureAdapterState: LegalStructureAdapterState = {
  editing: false,
  dirty: false,
  saving: false,
  draft: null,
  undoStack: [],
  error: null,
};

const MAX_UNDO_STEPS = 50;

interface UndoShortcutEvent {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

export function isLegalStructureUndoShortcut(
  event: UndoShortcutEvent,
): boolean {
  return (
    event.key.toLowerCase() === "z"
    && (event.ctrlKey || event.metaKey)
    && !event.altKey
    && !event.shiftKey
  );
}

export function legalStructureAdapterReducer(
  state: LegalStructureAdapterState,
  action: LegalStructureAdapterAction,
): LegalStructureAdapterState {
  switch (action.type) {
    case "edit":
      return {
        editing: true,
        dirty: state.dirty,
        saving: false,
        draft: state.draft ?? structuredClone(action.structure),
        undoStack: state.undoStack,
        error: null,
      };
    case "change": {
      const previousStructure = state.draft ?? action.persistedStructure;
      const changed = !legalStructuresHaveSameWritableState(
        previousStructure,
        action.structure,
      );
      const dirty = !legalStructuresHaveSameWritableState(
        action.structure,
        action.persistedStructure,
      );
      return {
        ...state,
        dirty,
        draft: state.editing || dirty ? action.structure : null,
        undoStack: changed
          ? [
              ...state.undoStack,
              structuredClone(previousStructure),
            ].slice(-MAX_UNDO_STEPS)
          : state.undoStack,
      };
    }
    case "undo": {
      const previousStructure = state.undoStack.at(-1);
      if (!previousStructure) return state;
      const dirty = !legalStructuresHaveSameWritableState(
        previousStructure,
        action.persistedStructure,
      );
      return {
        ...state,
        dirty,
        draft: state.editing || dirty ? previousStructure : null,
        undoStack: state.undoStack.slice(0, -1),
        error: null,
      };
    }
    case "save_started":
      return { ...state, saving: true, error: null };
    case "save_failed":
      return { ...state, saving: false, error: action.error };
    case "cancel":
    case "save_succeeded":
      return initialLegalStructureAdapterState;
  }
}

export function buildLegalStructureSavePayload(
  resource: BuilderLegalStructureResponse,
  structure: BuilderLegalStructure,
  attachmentIdsByEntity: Readonly<Record<string, readonly string[]>> = {},
): SaveBuilderLegalStructureRequest {
  const persistedEntityIds = new Set(
    resource.structure.entities.map((entity) => entity.id),
  );
  return {
    source_revision_id: resource.source_revision_id,
    expected_revision_id: resource.revision_id,
    expected_fingerprint: resource.fingerprint,
    structure: {
      presentation_currency: structure.presentation_currency,
      presentation_currency_confirmed:
        structure.presentation_currency_confirmed,
      groups: structure.groups.map(({ id, name, position }) => ({
        id,
        name,
        position,
      })),
      entities: structure.entities.map((entity) => {
        const attachmentDocumentIds = persistedEntityIds.has(entity.id)
          ? []
          : [...(attachmentIdsByEntity[entity.id] ?? [])];
        return {
          id: entity.id,
          name: entity.name,
          activity_summary: entity.activity_summary,
          operating_currency: entity.operating_currency,
          deal_events: entity.deal_events,
          transaction_perimeter: entity.transaction_perimeter,
          display_group_id: entity.display_group_id,
          position: entity.position,
          parent_entity_id: entity.parent_entity_id,
          relationship_type: entity.relationship_type,
          active: entity.active,
          ...(attachmentDocumentIds.length > 0
            ? { attachment_document_ids: attachmentDocumentIds }
            : {}),
        };
      }),
    },
  };
}

export function legalStructureSaveErrorMessage(error: unknown): string {
  if (!(error instanceof BackendError)) {
    return "The legal structure could not be saved.";
  }
  if (error.status === 409) {
    return "The legal structure changed elsewhere. The latest revision was reloaded.";
  }
  if (!error.bodyText) return error.message;
  try {
    const body = JSON.parse(error.bodyText) as { detail?: unknown };
    if (typeof body.detail === "string") return body.detail;
    if (
      body.detail
      && typeof body.detail === "object"
      && "message" in body.detail
      && typeof body.detail.message === "string"
    ) {
      return body.detail.message;
    }
  } catch {
    return error.message;
  }
  return error.message;
}
